import { AudioPresets, LocalAudioTrack, LocalVideoTrack, Track } from 'livekit-client'
import type { TrackPublishOptions } from 'livekit-client'
import * as Sentry from '@sentry/nuxt'
import { SCREEN_SHARE_PROFILES } from './profiles'
import type { ScreenShareEncodingProfile } from './profiles'
import type { CallSession } from './session'
import type { ScreenShareQuality } from './types'

/**
 * All the screen-share-specific mechanics `LiveKitManager` delegates to:
 * profile computation, publish options, and applying constraints/encoding
 * parameters to a live track. `LiveKitManager` still owns the media queue,
 * `state` mutation, and staleness checks around these calls — this module
 * only knows how to talk to LiveKit/WebRTC, not when it's safe to do so.
 */

export function effectiveScreenProfile(quality: ScreenShareQuality, requestedFps: number): ScreenShareEncodingProfile {
  const profile = SCREEN_SHARE_PROFILES[quality]
  // Clamp to each quality's own ceiling (see `SCREEN_SHARE_PROFILES` in profiles.ts): 720p/1080p
  // top out at 30, so 5/15/30 are all reachable; 1440p tops out at 15 (its `contentHint: 'text'`
  // profile trades framerate for resolution/bitrate), so a requested 30 silently settles at 15
  // — this is also why the 30 FPS toggle is disabled for 1440p in CallControls.vue.
  const maxFramerate = Math.min(requestedFps, profile.maxFramerate)
  return { ...profile, maxFramerate }
}

export function screenPublishOptions(profile: ScreenShareEncodingProfile) {
  const isMotion = profile.contentHint === 'motion'
  return {
    screenShareEncoding: { maxBitrate: profile.maxBitrate, maxFramerate: profile.maxFramerate },
    simulcast: false,
    // LiveKit forces contentHint="motion" for VP9/AV1 screen SVC. VP8 keeps
    // the requested text/detail hint and one full-resolution layer.
    videoCodec: isMotion ? ('h264' as const) : ('vp8' as const),
    backupCodec: false as const,
    degradationPreference: profile.degradationPreference,
  }
}

export async function applyScreenShareConstraints(track: MediaStreamTrack, profile: ScreenShareEncodingProfile) {
  const capabilities = track.getCapabilities?.()
  const width = Math.min(profile.width, capabilities?.width?.max ?? profile.width, 3840)
  const height = Math.min(profile.height, capabilities?.height?.max ?? profile.height, 2160)
  try {
    await track.applyConstraints({
      width: { ideal: width },
      height: { ideal: height },
      frameRate: { ideal: profile.maxFramerate, max: profile.maxFramerate },
    })
  } catch (error) {
    console.warn('[screenShareController] Screen constraints partially rejected', error)
    await track.applyConstraints({ frameRate: { ideal: profile.maxFramerate, max: profile.maxFramerate } })
      .catch(fallback => {
        console.warn('[screenShareController] Screen FPS rejected', fallback)
        Sentry.captureException(fallback, { level: 'warning', extra: { action: 'apply_screen_share_constraints_fallback' } })
      })
  }
}

export async function applyScreenSenderParameters(sender: RTCRtpSender, profile: ScreenShareEncodingProfile) {
  const apply = async (withPreference: boolean, withScale: boolean) => {
    const parameters = sender.getParameters()
    const encoding = parameters.encodings.at(-1)
    if (!encoding) return
    encoding.maxBitrate = profile.maxBitrate
    encoding.maxFramerate = profile.maxFramerate
    if (withScale && encoding.scaleResolutionDownBy !== undefined) encoding.scaleResolutionDownBy = 1
    if (withPreference) parameters.degradationPreference = profile.degradationPreference
    await sender.setParameters(parameters)
  }
  try {
    await apply(true, true)
  } catch {
    try {
      await apply(true, false)
    } catch (error) {
      console.warn('[screenShareController] Sender preference rejected', error)
      await apply(false, false).catch(fallback => {
        console.error('[screenShareController] Sender update failed', fallback)
        Sentry.captureException(fallback, { extra: { action: 'apply_screen_sender_parameters_fallback' } })
      })
    }
  }
}

/** Reapplies a profile (quality/FPS/preset change) to an already-published screen-share track. */
export async function reconfigureScreenShareTrack(track: LocalVideoTrack, profile: ScreenShareEncodingProfile) {
  track.mediaStreamTrack.contentHint = profile.contentHint
  await applyScreenShareConstraints(track.mediaStreamTrack, profile)
  if (track.sender) await applyScreenSenderParameters(track.sender, profile)
}

/** LiveKit's built-in `getDisplayMedia`-backed screen share (used when no custom capture stream is supplied). */
export async function enableBuiltinScreenShare(session: CallSession, profile: ScreenShareEncodingProfile) {
  await session.room.localParticipant.setScreenShareEnabled(
    true,
    { resolution: { width: profile.width, height: profile.height, frameRate: profile.maxFramerate }, contentHint: profile.contentHint },
    screenPublishOptions(profile),
  )
}

/** Stops whichever kind of screen share (custom capture or built-in) is currently active. */
export async function disableScreenShare(session: CallSession) {
  if (session.hasCustomScreenShare()) {
    await session.stopScreenShare()
  } else {
    await session.room.localParticipant.setScreenShareEnabled(false).catch(() => undefined)
  }
}

/**
 * Publishes a custom-capture `MediaStream` (from the native live-capture
 * pipeline, see `ScreenShareModal.vue`/`commands/screen_capture.rs`) as
 * screen-share (+ optional screen-audio) tracks. On any publish failure,
 * unpublishes/stops whatever was already published and stops the source
 * stream, then rethrows — the caller owns nothing on failure.
 */
export async function publishCustomScreenShare(
  session: CallSession,
  stream: MediaStream,
  profile: ScreenShareEncodingProfile,
  includeAudio: boolean,
  assertCurrent: () => void,
): Promise<Array<LocalVideoTrack | LocalAudioTrack>> {
  const mediaVideo = stream.getVideoTracks()[0]
  if (!mediaVideo) {
    stream.getTracks().forEach(track => track.stop())
    throw new Error('Выбранный источник не содержит видео')
  }
  await applyScreenShareConstraints(mediaVideo, profile)
  mediaVideo.contentHint = profile.contentHint

  const localTracks: Array<LocalVideoTrack | LocalAudioTrack> = []
  try {
    const videoTrack = new LocalVideoTrack(mediaVideo, undefined, true)
    localTracks.push(videoTrack)
    await session.room.localParticipant.publishTrack(videoTrack, {
      source: Track.Source.ScreenShare,
      name: 'screen',
      stream: 'screen',
      ...screenPublishOptions(profile),
    } satisfies TrackPublishOptions)

    const mediaAudio = stream.getAudioTracks()[0]
    if (mediaAudio) {
      const audioTrack = new LocalAudioTrack(mediaAudio, undefined, true)
      localTracks.push(audioTrack)
      if (!includeAudio) await audioTrack.mute()
      await session.room.localParticipant.publishTrack(audioTrack, {
        source: Track.Source.ScreenShareAudio,
        name: 'screen-audio',
        stream: 'screen',
        audioPreset: AudioPresets.musicHighQualityStereo,
        dtx: false,
        red: true,
      })
    }
    assertCurrent()
    return localTracks
  } catch (error) {
    for (const track of localTracks) {
      await session.room.localParticipant.unpublishTrack(track, false).catch(() => undefined)
      track.stop()
    }
    stream.getTracks().forEach(track => track.stop())
    throw error
  }
}
